/** @jsx h */
import { component, h } from 'literaljs';
import { playBloop, initAudio } from '../../utils/sounds';
import { copyToClipboard } from '../../utils/clipboard';
import { showToast, toast } from '../../utils/toast';
import HomeHeroOrb from '../HomeHeroOrb';

export default component({
	name: 'HomePage',
	state: {},
	mounted() {
		// Initialize audio on first user interaction (browser autoplay policy)
		initAudio();
	},
	methods() {
		return {
			copyWithFeedback(text, label, event) {
				copyToClipboard(text,
					() => { showToast(`Copied ${label} to clipboard`, { type: 'success' }); playBloop(440); },
					() => showToast(`Failed to copy ${label}`, { type: 'error' })
				);
			}
		};
	},
	render() {
		const features = [
			{ title: 'Keyed Reconciliation', desc: 'Cursor-based algorithm preserves DOM identity across reordering and swaps.' },
			{ title: 'Instance Persistence', desc: 'Component instances persist across renders — stable method refs enable row-level memoization (8.1.9).' },
			{ title: 'WeakMap Events', desc: 'Event delegation with boolean flag short-circuit for instant dispatch.' },
			{ title: 'App Containers', desc: 'Isolated instances with no global state. Multiple apps on one page.' },
			{ title: 'Auto-Keyed Siblings', desc: 'Components auto-keyed by render position. State isolation by default.' },
			{ title: 'Three Syntaxes', desc: 'JSX, Hyperscript, or Object markup. Use what fits your workflow.' }
		];

		const benchmarkData = [
			{ name: 'select row', v7: '~22ms', v8: '~0.04ms', change: '6.2× faster' },
			{ name: 'update every 10th', v7: '~186ms', v8: '~85ms', change: '3.2× faster' },
			{ name: 'swap rows', v7: '—', v8: '~0.3ms', change: '3.0× faster' },
			{ name: 'remove row', v7: '~74ms', v8: '~5ms', change: '2.2× faster' },
			{ name: 'replace 1,000 rows', v7: '~65ms', v8: '~6ms', change: '−91%' },
			{ name: 'clear rows', v7: '~3ms', v8: '~0.01ms', change: '−99.7%' }
		];

		return (
			<div class="page-home"
				events={{
					click: (e) => {
						// Play bloop on any button click
						if (e.target.closest('.btn') || e.target.closest('.install-card')) {
							playBloop(0.9 + Math.random() * 0.3);
						}
					},
					keydown: (e) => {
						// Keyboard activation: bloop on Enter/Space for .btn or .install-card.
						// e.repeat filters auto-repeat; .btn anchors fire click natively on Enter
						// which would double-fire (minor — accepted per plan).
						if (e.repeat) return;
						if (e.key !== 'Enter' && e.key !== ' ') return;
						if (e.target.closest('.btn') || e.target.closest('.install-card')) {
							playBloop(0.9 + Math.random() * 0.3);
						}
					}
				}}
			>
				{/* Hero */}
				<section class="hero">
					<HomeHeroOrb />
					<div class="hero-content">
						<div class="hero-badge">
							<span class="badge-dot"></span> v8.1.11 Released
						</div>
						<h1 class="hero-title">
							Tiny framework.<br />
							<span class="highlight">Big feelings.</span>
						</h1>
						<p class="hero-subtitle">
							LiteralJS is a 5.4 kB zero-dependency library with keyed
							reconciliation, WeakMap events, persistent instances, and render memoization.
							Build reactive UIs that feel snappy and look adorable.
						</p>
						<div class="hero-actions">
							<a href="#quickstart" class="btn -primary">
								<span aria-hidden="true">🚀</span> Get Started
							</a>
							<a href="https://gitlab.com/mikeysax/LiteralJS-Starter" target="_blank" class="btn -secondary">
								<span aria-hidden="true">💜</span> Starter Template
							</a>
						</div>

						<div class="hero-stats">
							<div class="stat">
								<div class="stat-value">5.4 kB</div>
								<div class="stat-label">Gzipped</div>
							</div>
							<div class="stat">
								<div class="stat-value">Zero</div>
								<div class="stat-label">Dependencies</div>
							</div>
							<div class="stat">
								<div class="stat-value">Modern</div>
								<div class="stat-label">ES2018+</div>
							</div>
						</div>
					</div>
				</section>

				{/* Features */}
				<section id="features" class="features">
					<div class="section-header">
						<h2><span class="emoji" aria-hidden="true">✨</span> What makes it special</h2>
						<p class="section-subtitle">Built for speed. Designed for smiles.</p>
					</div>
					<div class="feature-grid stagger-children">
						{features.map((f, i) => (
							<div class="feature-card" key={i}>
								<div class="feature-icon">{f.title.charAt(0)}</div>
								<h3>{f.title}</h3>
								<p>{f.desc}</p>
							</div>
						))}
					</div>
				</section>

				{/* v7 vs v8 Benchmarks */}
				<section id="benchmarks" class="benchmarks">
					<div class="section-header">
						<h2><span class="emoji" aria-hidden="true">🏎️</span> Performance</h2>
						<p class="section-subtitle">js-framework-benchmark results (Chrome, 4× CPU throttle) + JSDOM baseline</p>
					</div>
					<div class="benchmark-table">
						<table>
							<thead><tr><th>Benchmark</th><th>v7</th><th>v8.1.11</th><th class="th-change">Change</th></tr></thead>
							<tbody>
								{benchmarkData.map((row, i) => (
									<tr key={i}>
										<td class="td-name">{row.name}</td>
										<td>{row.v7}</td>
										<td class="td-fast">{row.v8}</td>
										<td class="td-change">{row.change}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</section>

				{/* Quick Start */}
				<section id="quickstart" class="quickstart">
					<div class="section-header">
						<h2><span class="emoji" aria-hidden="true">🎯</span> Quick Start</h2>
						<p class="section-subtitle">Install, mount, done.</p>
					</div>
					<div class="code-block">
						<div class="code-header"><span>JSX</span></div>
						<div class="code-body">
							<pre><code>{`/** @jsx h */
import { h, component, App } from 'literaljs';

const Counter = component({
  name: 'Counter',
  state: { count: 0 },
  render() {
    const { count } = this.getState();
    return (
      <div>
        <p>Count: {count}</p>
        <button
          events={{ click: () => this.setState({ count: count + 1 }) }}
        >
          Increment
        </button>
      </div>
    );
  }
});

new App(Counter, {}).mount('app');`}</code></pre>
						</div>
					</div>

					<div class="install-grid">
						<div class="install-card"
							events={{ click: (e) => { e.preventDefault(); this.copyWithFeedback('npm install literaljs', 'npm command', e); } }}
							style={{ cursor: 'pointer' }}
						>
							<div class="install-label"><span aria-hidden="true">📋</span> npm — Click to copy</div>
							<div class="install-command">npm install literaljs</div>
						</div>
						<div class="install-card"
							events={{ click: (e) => { e.preventDefault(); this.copyWithFeedback('https://unpkg.com/literaljs/build/index.umd.js', 'CDN URL', e); } }}
							style={{ cursor: 'pointer' }}
						>
							<div class="install-label"><span aria-hidden="true">📋</span> CDN — Click to copy</div>
							<div class="install-command">unpkg.com/literaljs/build/index.umd.js</div>
						</div>
					</div>
				</section>

				{/* Footer (CTA only — shared footer is rendered by index.js) */}
				<footer class="site-footer site-footer-home">
					<div class="footer-content">
						<h2 class="footer-heading">Start building today</h2>
						<p class="footer-subtitle">Clone the starter template and ship in minutes.</p>
						<div class="footer-actions">
							<a href="https://gitlab.com/mikeysax/LiteralJS" target="_blank" rel="noopener noreferrer" class="btn -secondary">GitLab</a>
							<a href="https://www.npmjs.com/package/literaljs" target="_blank" rel="noopener noreferrer" class="btn -secondary">npm</a>
							<a href="https://gitlab.com/mikeysax/LiteralJS" target="_blank" rel="noopener noreferrer" class="btn -primary">Documentation</a>
						</div>
					</div>
				</footer>
			</div>
		);
	}
});